"use client";

import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { FaArrowDown, FaCheck } from "react-icons/fa6";
import type { Locale } from "@/lang";
import { useLocale } from "@/app/providers/IntlProvider";
import { trackStoryStep } from "@/lib/analytics/track";
import { BAND_WIDTH, SECTION_GUTTER } from "../widths";
import { StoryLottie, phoneStageClass } from "./StoryLottie";
import { useIsDesktop, usePinnedOffset } from "./usePinnedProgress";

type SlideId = "problem" | "build" | "action";

/** One slide of the tour: one idea, one picture. */
type Slide = { id: SlideId; scene: string };

/**
 * The home argument's three main beats, in the order a visitor raises objections (see the
 * sales argument): the problem (organizing has become a second job: either a tool to build,
 * or five tools to connect in your head; Flots is neither), the answer (nothing to build,
 * nothing to learn), the proof (from meeting to action). Everything profile-specific lives
 * in the "Who are you?" section below.
 */
const SLIDES: Slide[] = [
  { id: "problem", scene: "scatter" },
  { id: "build", scene: "tasks" },
  { id: "action", scene: "notes" },
];

/** The vise's three statements on the first slide, revealed one per scroll beat. */
const VISE = ["heavy", "scattered", "flots"] as const;

/** Last frame of the scatter scene. */
const SCATTER_END = 197;

/**
 * Frame the scatter scene stops on for each vise statement (tools/lottie/scenes/scatter.mjs,
 * 60 fps): the heavy platform still standing (0.5 s), you holding the strings (1.85 s),
 * then the Flots window.
 */
const VISE_FRAME = [30, 111, SCATTER_END] as const;

/** Pale washes of the brand palette, one per slide, cross-faded on the panel. */
const TINT: Record<SlideId, string> = {
  problem: "var(--grad-story-problem)",
  build: "var(--grad-story-build)",
  action: "var(--grad-story-action)",
};

/**
 * Slide heading: the problem is stated, not asked, so every slide ends on the brand full
 * stop of the launch film.
 */
function SlideTitle({ id }: { id: SlideId }) {
  return (
    <>
      <FormattedMessage id={id === "problem" ? "problem.title" : `story.group.${id}`} />
      <span className="text-brand">.</span>
    </>
  );
}

/**
 * Slide body: the vise's three statements, or a pillar's one-line proof and its text.
 * `step` is the last vise statement revealed (the ones after it are folded away and unfold
 * when their beat comes); omitted, all three show.
 */
function SlideBody({ id, size, step = VISE.length - 1 }: { id: SlideId; size: "lg" | "md"; step?: number }) {
  const lead = size === "lg" ? "text-lg xl:text-xl 2xl:text-2xl min-[1920px]:text-[1.75rem]" : "text-lg";
  const text = size === "lg" ? "text-[15px] xl:text-base 2xl:text-lg min-[1920px]:text-xl" : "text-[15px]";
  // In the pinned panel on short screens (its height is capped by the viewport and by
  // half its width), the vise keeps its three statements and only Flots' answer is spelled
  // out; the two pains' explanations come back once there is room.
  const roomy = size === "lg" ? "hidden [@media(min-height:860px)_and_(min-width:1280px)]:block" : "";
  if (id === "problem") {
    return (
      <div className="flex flex-col">
        {VISE.map((k, i) => (
          // A statement not revealed yet takes no room (its row collapses to 0fr), then
          // unfolds: the centred sidebar grows smoothly instead of showing a hole.
          <div
            key={k}
            className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out motion-reduce:transition-none ${
              i <= step ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
            }`}
          >
          <div className="min-h-0 overflow-hidden">
          <div className={i > 0 ? "pt-3.5 xl:pt-4" : ""}>
          <div className={k === "flots" ? "border-l-[3px] border-blue-main pl-4" : "pl-[19px]"}>
            <p className={`font-semibold leading-snug ${lead} ${k === "flots" ? "text-brand-ink" : "text-title-blue"}`}>
              <FormattedMessage id={`problem.${k}.title`} />
            </p>
            <p className={`mt-1 leading-relaxed text-text-main ${text} ${k === "flots" ? "" : roomy}`}>
              <FormattedMessage id={`problem.${k}.desc`} />
            </p>
          </div>
          </div>
          </div>
          </div>
        ))}
      </div>
    );
  }
  const beat = id === "build" ? "tasks" : "notes";
  return (
    <div>
      <p className={`font-semibold leading-snug text-title-blue ${lead}`}>
        <FormattedMessage id={`story.${beat}.title`} />
      </p>
      <p className={`mt-3 leading-relaxed text-text-main ${text}`}>
        <FormattedMessage id={`story.${beat}.desc`} />
      </p>
    </div>
  );
}

/**
 * The home product tour: three slides.
 *
 * Desktop: one tall section with a pinned, tinted panel. The scroll picks the active slide
 * (one band of the section per slide); the panel shows its title and text, a labelled
 * progress rail, and the stage plays the slide's animation once. Nothing is scrubbed frame
 * by frame: slides trigger, animations run in real time.
 * Phones and tablets: one card per slide, each animation plays when mostly visible.
 */
export function ScrollStory() {
  const isDesktop = useIsDesktop();
  return isDesktop ? <DesktopStory /> : <StackedStory />;
}

// ── Desktop: pinned panel ─────────────────────────────────────────────────────────

/**
 * Scroll beats of the pinned panel, in viewport heights. The first slide takes three
 * beats, one per vise statement (the two bad options before Flots); each pillar takes one.
 */
const BEATS: { slide: number; vise?: number; vh: number }[] = [
  // Short: the first option (a tool to build) is already on screen while the panel arrives.
  { slide: 0, vise: 0, vh: 18 },
  { slide: 0, vise: 1, vh: 32 },
  { slide: 0, vise: 2, vh: 40 },
  { slide: 1, vh: 50 },
  { slide: 2, vh: 50 },
];

/** Where each beat starts, in viewport heights from the moment the panel pins. */
const BEAT_START = BEATS.map((_, k) => BEATS.slice(0, k).reduce((sum, b) => sum + b.vh, 0));

/** Total scroll length of the beats. */
const BEATS_VH = BEATS.reduce((sum, b) => sum + b.vh, 0);

/** Scroll position (vh from the pin) that shows a slide: the middle of its first beat. */
const slideAnchorVh = (slide: number) => {
  const k = BEATS.findIndex((b) => b.slide === slide);
  return BEAT_START[k] + BEATS[k].vh / 2;
};

function DesktopStory() {
  const intl = useIntl();
  const { locale } = useLocale();
  const { sectionRef, panelRef, offset, stickyTop } = usePinnedOffset<HTMLElement, HTMLDivElement>(true);
  const [vhPx, setVhPx] = useState(0);

  useEffect(() => {
    const sync = () => setVhPx(window.innerHeight / 100);
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  // -1 while the panel is still arriving: the first picture shows, nothing plays yet.
  let beat = -1;
  if (vhPx && offset >= 0) {
    const at = offset / vhPx;
    beat = BEAT_START.findLastIndex((start) => at >= start);
  }
  const active = beat < 0 ? -1 : BEATS[beat].slide;
  const shown = SLIDES[Math.max(0, active)];
  // Vise statements revealed so far: only the question while the panel arrives.
  const viseStep = beat < 0 ? 0 : (BEATS[beat].vise ?? VISE.length - 1);
  const scatterTarget = beat < 0 ? 0 : active === 0 ? VISE_FRAME[viseStep] : SCATTER_END;
  const atEnd = beat === BEATS.length - 1;

  useEffect(() => {
    if (active >= 0) trackStoryStep(SLIDES[active].id, active);
  }, [active]);

  const scrollToSlide = (k: number) => {
    const section = sectionRef.current;
    if (!section || !vhPx) return;
    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: sectionTop - stickyTop + slideAnchorVh(k) * vhPx, behavior: "smooth" });
  };

  return (
    <section
      ref={sectionRef}
      id="story"
      aria-labelledby="story-heading"
      className={`relative ${SECTION_GUTTER}`}
      style={
        {
          "--story-top": "calc(var(--promo-h, 0px) + 7rem)",
          // The panel fills the viewport under the navbar, but never gets taller than half
          // its width (so the stage is filled instead of floating in a tall empty box).
          "--story-panel-h": "min(calc(100vh - var(--story-top) - 1.5rem), 50vw)",
          height: `calc(${BEATS_VH}vh + var(--story-panel-h))`,
        } as React.CSSProperties
      }
    >
      {/* Invisible anchors, one per slide, placed where that slide is centred (navbar links). */}
      {SLIDES.map((s, k) => (
        <div
          key={s.id}
          id={`story-${s.id}`}
          aria-hidden="true"
          className="pointer-events-none absolute left-0 h-px w-px"
          style={{ top: `calc(50vh - var(--story-top) + ${slideAnchorVh(k)}vh)` }}
        />
      ))}

      <div
        ref={panelRef}
        className={`sticky mx-auto ${BAND_WIDTH}`}
        style={{ top: "var(--story-top)", height: "var(--story-panel-h)" }}
      >
        <div className="relative h-full overflow-hidden rounded-[40px] ring-1 ring-blue-light">
          {SLIDES.map((s) => (
            <div
              key={s.id}
              aria-hidden="true"
              className="absolute inset-0 transition-opacity duration-700 ease-out"
              style={{ background: TINT[s.id], opacity: s.id === shown.id ? 1 : 0 }}
            />
          ))}

          <div className="relative grid h-full grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-center gap-8 px-10 py-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] xl:gap-14 xl:p-14 2xl:gap-16 2xl:p-16 min-[1920px]:gap-24 min-[1920px]:px-24">
            {/* Sidebar: the three slides as a numbered path, the current one opened. */}
            <SlideSidebar active={active} viseStep={viseStep} onJump={scrollToSlide} />

            {/* Stage: every scene stacked, the current one visible. Decorative. */}
            <div aria-hidden="true" className="relative h-full min-h-0">
              {SLIDES.map((s, k) => {
                const on = shown.id === s.id;
                const near = vhPx > 0 && offset > -200 * vhPx && Math.abs(k - Math.max(0, active)) <= 1;
                return (
                  <div key={s.id} className="story-scene absolute inset-0" data-active={on}>
                    <StoryLottie
                      name={s.scene}
                      locale={locale}
                      load={near}
                      active={on && active >= 0}
                      target={s.scene === "scatter" ? scatterTarget : undefined}
                      label={intl.formatMessage({ id: `story.part.${s.id}` })}
                      className="h-full w-full"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <ScrollCue done={atEnd} />
        </div>
      </div>
    </section>
  );
}

/**
 * Scroll cue in the panel's lower right corner: a white pill with a bouncing arrow and
 * « keep scrolling », so a pinned panel never reads as the page being stuck. It fades out
 * on the last beat. Decorative: the sidebar is the navigation.
 */
function ScrollCue({ done }: { done: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute bottom-5 right-5 flex items-center gap-2.5 rounded-full bg-surface py-1.5 pl-1.5 pr-4 text-sm font-semibold text-title-blue shadow-lg shadow-blue-main/15 ring-1 ring-blue-light transition duration-500 ease-out xl:bottom-7 xl:right-7 xl:text-[15px] ${
        done ? "translate-y-2 opacity-0" : "opacity-100"
      }`}
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-main text-white xl:h-9 xl:w-9">
        <FaArrowDown className="scroll-cue-arrow text-xs xl:text-sm" />
      </span>
      <FormattedMessage id="story.scrollHint" />
    </div>
  );
}

/**
 * The panel's sidebar: the three slides as one vertical, numbered path. Passed slides are
 * ticked and joined by a filled line, the current one opens with its title and text, the
 * ones ahead stay as compact labels. The whole path is centred vertically, so the copy and
 * the stage share one axis. Each marker jumps to its slide.
 */
function SlideSidebar({ active, viseStep, onJump }: { active: number; viseStep: number; onJump: (k: number) => void }) {
  const intl = useIntl();
  const current = Math.max(0, active);
  return (
    <nav aria-label={intl.formatMessage({ id: "story.nav" })} className="min-h-0">
      <ol className="flex flex-col">
        {SLIDES.map((s, k) => {
          const on = k === current;
          const done = k < active;
          const last = k === SLIDES.length - 1;
          return (
            <li key={s.id} className="relative grid grid-cols-[2rem_minmax(0,1fr)] gap-x-4 xl:gap-x-5 min-[1920px]:grid-cols-[2.5rem_minmax(0,1fr)] min-[1920px]:gap-x-6">
              {/* Line to the next marker: brand colour once this slide is passed. */}
              {!last && (
                <span
                  aria-hidden="true"
                  className={`absolute bottom-0 left-[calc(1rem-1px)] top-9 w-0.5 min-[1920px]:left-[calc(1.25rem-1px)] min-[1920px]:top-11 rounded-full transition-colors duration-500 ${
                    done ? "bg-blue-main" : "bg-blue-sec/50"
                  }`}
                />
              )}
              <button
                type="button"
                onClick={() => onJump(k)}
                aria-current={on ? "step" : undefined}
                aria-label={intl.formatMessage({ id: `story.part.${s.id}` })}
                className={`relative z-10 mt-0.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-sm font-bold min-[1920px]:mt-1.5 min-[1920px]:h-10 min-[1920px]:w-10 min-[1920px]:text-base tabular-nums transition duration-300 ${
                  on || done
                    ? "bg-blue-main text-white shadow-md shadow-blue-main/30"
                    : "bg-surface text-sec-blue ring-1 ring-blue-sec hover:ring-blue-main"
                }`}
              >
                {done ? <FaCheck aria-hidden="true" className="text-xs" /> : k + 1}
              </button>
              <div className={last ? "" : "pb-5 xl:pb-8 2xl:pb-9 min-[1920px]:pb-12"}>
                {on ? (
                  <div key={s.id} className="story-in">
                    <h2
                      id="story-heading"
                      className="text-balance text-[1.75rem] font-bold leading-[1.08] tracking-[-0.025em] text-title-blue xl:text-[2.4rem] 2xl:text-5xl min-[1920px]:text-[4rem]"
                    >
                      <SlideTitle id={s.id} />
                    </h2>
                    <div className="mt-5 xl:mt-6 min-[1920px]:mt-8">
                      <SlideBody id={s.id} size="lg" step={viseStep} />
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => onJump(k)}
                    className="cursor-pointer pt-1 text-left text-base font-semibold leading-snug text-gray-main transition-colors hover:text-title-blue xl:text-lg min-[1920px]:pt-2 min-[1920px]:text-2xl"
                  >
                    <FormattedMessage id={`story.part.${s.id}`} />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ── Phones and tablets: one card per slide ────────────────────────────────────────

/**
 * Tracks one element's visibility.
 *
 * @param rootMargin - IntersectionObserver root margin.
 * @param threshold - Visible ratio that counts as "in view".
 * @returns A ref to attach and whether the element is in view.
 */
function useInView<T extends Element>(rootMargin: string, threshold = 0) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { rootMargin, threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, threshold]);
  return [ref, inView] as const;
}

function StackedStory() {
  const { locale } = useLocale();
  return (
    <section id="story" aria-labelledby="story-heading" className="px-4 pb-8 pt-4 sm:px-8">
      <div className="mx-auto flex max-w-5xl flex-col gap-14 sm:gap-16">
        {SLIDES.map((s, k) => (
          <div key={s.id} id={`story-${s.id}`} className="scroll-mt-28">
            <h2
              id={k === 0 ? "story-heading" : undefined}
              className="mb-6 text-balance text-[2rem] font-bold leading-[1.1] tracking-[-0.02em] text-title-blue sm:text-4xl"
            >
              <SlideTitle id={s.id} />
            </h2>
            <SlideCard slide={s} index={k} locale={locale} />
          </div>
        ))}
      </div>
    </section>
  );
}

/** One slide as a card: animation and text; side by side from tablet width. */
function SlideCard({ slide, index, locale }: { slide: Slide; index: number; locale: Locale }) {
  const intl = useIntl();
  const [nearRef, near] = useInView<HTMLElement>("60% 0px 60% 0px");
  const [playRef, playing] = useInView<HTMLDivElement>("0px", 0.6);

  useEffect(() => {
    if (playing) trackStoryStep(slide.id, index);
  }, [playing, slide.id, index]);
  return (
    <article
      ref={nearRef}
      className="overflow-hidden rounded-[28px] ring-1 ring-blue-light md:grid md:grid-cols-[minmax(0,6fr)_minmax(0,5fr)] md:items-center"
      style={{ background: TINT[slide.id] }}
    >
      <div ref={playRef} aria-hidden="true" className={`relative aspect-[5/4] w-full ${phoneStageClass(slide.scene)}`}>
        <StoryLottie
          name={slide.scene}
          locale={locale}
          load={near}
          active={playing}
          label={intl.formatMessage({ id: `story.part.${slide.id}` })}
          className="absolute inset-0"
        />
      </div>
      <div className="px-6 pb-7 pt-1 md:py-8 md:pl-2 md:pr-8">
        <SlideBody id={slide.id} size="md" />
      </div>
    </article>
  );
}
